import { PropsWithChildren, ReactNode, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";

import OutsideAlerter from "@/components/hoc/OutsideAlerter";
import { easeQuartOut } from "@/utils/constants";

/** Props for {@link FilterItem} */
type Props =
  | {
      /** Always shown label of the filter dropdown */
      label: string;
      /** Smaller than default */
      small?: boolean;
      /** Optional JSX Element positioned left of the label */
      lefLabelChild?: JSX.Element;
      /** Sets the filter in toggle only mode (no dropdown). */
      toggleOnly?: never;
      /** Called when the user toggles the filter */
      onToggle?: never;
      /** Content of the dropdown */
      children: ReactNode;
    }
  | {
      /** Always shown label of the filter dropdown */
      label: string;
      /** Smaller than default */
      small?: boolean;
      /** Optional JSX Element positioned left of the label */
      lefLabelChild?: JSX.Element;
      /** Sets the filter in toggle only mode (no dropdown). */
      toggleOnly?: true;
      /** Called when the user toggles the filter */
      onToggle: () => void;
    };

/**
 * Renders filter item label that can animate open/close
 * on click showing `props.children`
 *
 * @param props - Component props
 */
export default function FilterItem({
  label,
  small,
  lefLabelChild,
  toggleOnly,
  onToggle,
  children,
}: PropsWithChildren<Props>) {
  const [show, setShow] = useState(false);

  const labelRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      ref={labelRef}
      className={clsx(
        small ? "h-7 lg:h-9 px-3" : "h-10 px-3",
        show ? "bg-dark-hover" : "bg-dark-stroke-clear hover:bg-dark-hover",
        "border border-dark-6",
        "flex items-center rounded-md cursor-pointer lg:relative flex-shrink-0"
      )}
      onClick={() => (toggleOnly ? onToggle?.() : setShow((s) => !s))}
    >
      {lefLabelChild}
      <div
        className={clsx(
          small
            ? "text-13 leading-1 lg:text-sm lg:leading-1 mr-1.5"
            : "text-sm lg:text-15 2xl:text-base mr-2",
          "flex-shrink-0 capitalize font-medium"
        )}
      >
        {label}
      </div>
      {!toggleOnly && !!children && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="9"
          height="6"
          viewBox="0 0 9 6"
          fill="none"
          className="mt-0.5 flex-shrink-0"
        >
          <path
            d="M1 1.5L3.74742 4.63991C4.14584 5.09524 4.85417 5.09524 5.25258 4.63991L8 1.5"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="stroke-white"
          />
        </svg>
      )}
      {!toggleOnly && !!children && (
        <AnimatePresence>
          {show && (
            <motion.div
              key="arrow"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: {
                  opacity: 1,
                  y: 0,
                  x: -8,
                  transition: { duration: 0.2, delay: 0.2, ease: [0.04, 0.62, 0.23, 0.98] },
                },
                collapsed: { opacity: 0, y: 8, x: -8, transition: { delay: 0, duration: 0 } },
              }}
              className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 z-110"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="15" viewBox="0 0 26 15" fill="none">
                <path
                  d="M10.7042 1.2592C12.1342 0.474851 13.8658 0.474851 15.2958 1.2592L22.6453 5.2903C26.9766 7.6659 25.2895 14.25 20.3495 14.25H5.65046C0.710527 14.25 -0.976547 7.66589 3.35467 5.2903L10.7042 1.2592Z"
                  className="fill-dark-6"
                />
              </svg>
            </motion.div>
          )}
          {show && (
            <motion.div
              key="dropdown"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { y: 0 },
                collapsed: { y: -16 },
              }}
              transition={{ duration: 0.3, ease: easeQuartOut }}
              className="absolute left-0 top-[calc(100%+12px)] min-w-full z-110"
            >
              <OutsideAlerter
                callback={(e) => {
                  if (!labelRef.current || !labelRef.current.contains(e.target as HTMLElement))
                    setShow(false);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, backdropFilter: "blur(24px) opacity(0)" }}
                  animate={{ opacity: 1, backdropFilter: "blur(24px) opacity(1)" }}
                  exit={{ opacity: 0, backdropFilter: "blur(24px) opacity(0)" }}
                  transition={{ ease: easeQuartOut, duration: 0.3 }}
                  className={clsx(
                    "absolute inset-0 bg-dark-glass -z-10 bg-dark-3/80 rounded-lg shadow-tooltip",
                    "before:absolute before:inset-0 before:-z-10 before:rounded-lg before:backdrop-blur-xl"
                  )}
                />
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: easeQuartOut }}
                  className={clsx(
                    "rounded-lg p-4 lg:p-5 min-w-full flex flex-col",
                    "items-center justify-center cursor-default",
                    "before:border-2 before:bg-dark-stroke",
                    "before:border-gradient before:rounded-lg"
                  )}
                >
                  {children}
                </motion.div>
              </OutsideAlerter>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
